import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  listResendAttempts,
  exportResendAttempts,
  validateRecipients,
  type ResendAttemptsResult,
  type ExportResendAttemptsResult,
} from "@/utils/admin.functions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export const Route = createFileRoute("/_authenticated/admin/resend-attempts")({
  head: () => ({
    meta: [{ title: "Admin — Resend invoice attempts" }],
  }),
  component: AdminResendAttemptsPage,
  errorComponent: ({ error }) => (
    <div className="p-6 text-sm text-destructive">{error.message}</div>
  ),
  notFoundComponent: () => <div className="p-6">Not found</div>,
});

function csvEscape(v: unknown): string {
  const s = v === null || v === undefined ? "" : String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadAttemptsCsv(rows: { id: string; user_id: string; invoice_id: string; created_at: string; user_email: string | null }[]) {
  const headers = ["id", "created_at", "user_id", "user_email", "invoice_id"];
  const lines = [headers.join(",")];
  for (const r of rows) {
    lines.push([r.id, r.created_at, r.user_id, r.user_email ?? "", r.invoice_id].map(csvEscape).join(","));
  }
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const ts = new Date().toISOString().replace(/[:.]/g, "-");
  a.href = url;
  a.download = `resend-invoice-attempts-${ts}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function AdminResendAttemptsPage() {
  const [search, setSearch] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [filters, setFilters] = useState<{ search: string; from: string; to: string }>({
    search: "",
    from: "",
    to: "",
  });
  const [isExporting, setIsExporting] = useState(false);
  const [recipientsInput, setRecipientsInput] = useState("");
  const [recipientErrors, setRecipientErrors] = useState<string[]>([]);
  const fetchFn = useServerFn(listResendAttempts);
  const exportFn = useServerFn(exportResendAttempts);

  const toIso = (local: string) => (local ? new Date(local).toISOString() : undefined);


  const { data, isLoading, isFetching, refetch, error } = useQuery({
    queryKey: ["admin-resend-attempts", filters],
    queryFn: () =>
      fetchFn({
        data: {
          search: filters.search,
          limit: 200,
          from: toIso(filters.from),
          to: toIso(filters.to),
        },
      }),
  });

  const result = data as ResendAttemptsResult | undefined;
  const isError = result && "error" in result;

  const handleExportAll = async () => {
    setRecipientErrors([]);
    const { valid: recipients, errors } = validateRecipients(recipientsInput);
    if (errors.length > 0) {
      setRecipientErrors(errors);
      return;
    }
    setIsExporting(true);
    try {
      const res = (await exportFn({
        data: {
          search: filters.search,
          from: toIso(filters.from),
          to: toIso(filters.to),
          recipients,
        },
      })) as ExportResendAttemptsResult;
      if ("error" in res) {
        toast.error(res.error);
        return;
      }
      if (res.mode === "async") {
        const list = res.recipientEmails.join(", ");
        toast.success(
          `Match set is ~${res.estimatedCount.toLocaleString()} rows — too large for inline export. We'll email a download link to ${list} when it's ready.`,
          { duration: 8000 },
        );
        return;
      }
      if (res.rows.length === 0) {
        toast.message("No rows match the current filters.");
        return;
      }
      downloadAttemptsCsv(res.rows);
      toast.success(
        `Exported ${res.rows.length.toLocaleString()} row${res.rows.length === 1 ? "" : "s"}.`,
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Export failed");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="container mx-auto max-w-6xl space-y-6 p-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">Resend invoice attempts</h1>
        <p className="text-sm text-muted-foreground">
          Admin view of all resend attempts and current rate-limit counters.
        </p>
      </header>

      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setFilters({ search: search.trim(), from, to });
        }}
      >
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">Search</label>
          <Input
            placeholder="invoice_id or user_id…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-72"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">From</label>
          <Input type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">To</label>
          <Input type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <Button type="submit" disabled={isFetching}>
          Apply
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            setSearch("");
            setFrom("");
            setTo("");
            setFilters({ search: "", from: "", to: "" });
          }}
          disabled={isFetching}
        >
          Clear
        </Button>
        <Button type="button" variant="outline" onClick={() => refetch()} disabled={isFetching}>
          Refresh
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={!result || isError || result.attempts.length === 0}
          onClick={() => {
            if (!result || isError) return;
            downloadAttemptsCsv(result.attempts);
          }}
        >
          Export page
        </Button>
        <Button type="button" onClick={handleExportAll} disabled={isExporting}>
          {isExporting ? "Exporting…" : "Export all matching"}
        </Button>
      </form>

      <div className="flex flex-col gap-1">
        <label className="text-xs text-muted-foreground">
          Async export recipients (optional, comma-separated · max 5 · defaults to your account email)
        </label>
        <Input
          placeholder="e.g. alice@example.com, bob@example.com"
          value={recipientsInput}
          onChange={(e) => {
            setRecipientsInput(e.target.value);
            if (recipientErrors.length > 0) setRecipientErrors([]);
          }}
          onBlur={() => {
            const { errors } = validateRecipients(recipientsInput);
            if (errors.length > 0) setRecipientErrors(errors);
          }}
          className={`max-w-2xl ${recipientErrors.length > 0 ? "border-destructive focus-visible:ring-destructive" : ""}`}
        />
        {recipientErrors.length > 0 && (
          <ul className="text-xs text-destructive space-y-0.5">
            {recipientErrors.map((err, i) => (
              <li key={i}>{err}</li>
            ))}
          </ul>
        )}
      </div>

      {error && <p className="text-sm text-destructive">{(error as Error).message}</p>}
      {isError && (
        <p className="text-sm text-destructive">
          {(result as { error: string }).error}
        </p>
      )}

      {result && !isError && (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Limits{" "}
                <span className="text-xs font-normal text-muted-foreground">
                  per-invoice {result.limits.perInvoiceWindowSeconds}s window ·{" "}
                  {result.limits.perInvoiceMaxPerDay}/day · per-user{" "}
                  {result.limits.perUserMaxPerWindow}/
                  {Math.round(result.limits.perUserWindowSeconds / 60)}min
                </span>
              </CardTitle>
            </CardHeader>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Per-user counters</CardTitle>
            </CardHeader>
            <CardContent>
              {result.userCounters.length === 0 ? (
                <p className="text-sm text-muted-foreground">No activity in window.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Last 1h</TableHead>
                      <TableHead>Last 24h</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {result.userCounters.map((u) => {
                      const over = u.count_1h >= result.limits.perUserMaxPerWindow;
                      return (
                        <TableRow key={u.user_id}>
                          <TableCell className="font-mono text-xs">
                            <div>{u.user_email ?? "—"}</div>
                            <div className="text-muted-foreground">{u.user_id}</div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={over ? "destructive" : "secondary"}>
                              {u.count_1h} / {result.limits.perUserMaxPerWindow}
                            </Badge>
                          </TableCell>
                          <TableCell>{u.count_24h}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Per-invoice counters</CardTitle>
            </CardHeader>
            <CardContent>
              {result.invoiceCounters.length === 0 ? (
                <p className="text-sm text-muted-foreground">No activity in window.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Last 60s</TableHead>
                      <TableHead>Last 24h</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {result.invoiceCounters.map((i) => {
                      const dayOver = i.count_24h >= result.limits.perInvoiceMaxPerDay;
                      const winOver = i.count_60s >= 1;
                      return (
                        <TableRow key={`${i.user_id}-${i.invoice_id}`}>
                          <TableCell className="font-mono text-xs">{i.invoice_id}</TableCell>
                          <TableCell className="font-mono text-xs">{i.user_id}</TableCell>
                          <TableCell>
                            <Badge variant={winOver ? "destructive" : "secondary"}>
                              {i.count_60s}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={dayOver ? "destructive" : "secondary"}>
                              {i.count_24h} / {result.limits.perInvoiceMaxPerDay}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Recent attempts{" "}
                <span className="text-xs font-normal text-muted-foreground">
                  ({result.attempts.length})
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <p className="text-sm text-muted-foreground">Loading…</p>
              ) : result.attempts.length === 0 ? (
                <p className="text-sm text-muted-foreground">No attempts found.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>When</TableHead>
                      <TableHead>Invoice</TableHead>
                      <TableHead>User</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {result.attempts.map((a) => (
                      <TableRow key={a.id}>
                        <TableCell className="text-xs">
                          {new Date(a.created_at).toLocaleString()}
                        </TableCell>
                        <TableCell className="font-mono text-xs">{a.invoice_id}</TableCell>
                        <TableCell className="font-mono text-xs">
                          <div>{a.user_email ?? "—"}</div>
                          <div className="text-muted-foreground">{a.user_id}</div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
